// FR-3 timbre analysis. 借鉴 violin 项目:峰值快照 + 分步算法(基频+谐波),
// 而非长时间累计。超过阈值期间跟踪峰值帧(2048 样本,2 的幂)用于频谱显示与分析,
// 累计发音时长作为"长音"门槛。SNR 三级熔断 + HER + LPC 共振峰评分。

import type { Page } from '../page';
import { el, scoreRing } from '../components';
import { state, toast } from '../store';
import { showMicError } from '../mic';
import { yinDetect } from '../../audio/yin';
import { powerSpectrum } from '../../audio/fft';
import { harmonicEnergyRatio, estimateSNR } from '../../audio/spectrum';
import { extractFormants } from '../../audio/lpc';
import { herScore, resonanceScore, timbreScore, snrPolicy } from '../../assessment/score';

const FORMANT_TEMPLATE: [number, number, number] = [300, 1000, 2600]; // placeholder (T-11 unresolved)
const MAX_FREQ = 4000; // 频谱显示到 4kHz
const MIN_TONE_MS = 800; // 长音门槛(累计发音时长)

interface PeakSnapshot {
  samples: Float32Array;
  sampleRate: number;
  freq: number;
  rms: number;
}

export function timbrePage(): Page {
  let cleanup: (() => void) | null = null;
  return {
    mount(container) {
      let unsub: (() => void) | null = null;
      const dpr = window.devicePixelRatio || 1;

      const statusEl = el('div', { class: 'tiny muted', text: '拉一个稳定的长音，系统分析音色' });
      const startBtn = el('button', { class: 'primary', text: '开始录音' });
      const stopBtn = el('button', { class: 'ghost', text: '停止分析', style: 'display:none' });
      const resultCard = el('div');
      const progress = el('div', { class: 'tiny muted', style: 'display:none' });

      // ---- 阈值滑块 ----
      let volumeThresholdPct = 12;
      const thrSlider = el('input', { type: 'range', min: '0', max: '100', step: '1', value: String(volumeThresholdPct) });
      const thrVal = el('span', { class: 'tiny muted', text: `阈值 ${volumeThresholdPct}%` });
      thrSlider.oninput = () => { volumeThresholdPct = Number(thrSlider.value); thrVal.textContent = `阈值 ${volumeThresholdPct}%`; };
      const thrControl = el('div', { class: 'thr-control' }, [el('div', { class: 'thr-row' }, [el('span', { text: '音量阈值(超过才采集)' }), thrVal]), thrSlider]);

      // ---- 峰值快照状态(每段取峰值,段间冻结) ----
      let peakRms = 0;
      let snapshotFrame: PeakSnapshot | null = null;
      let wasBelow = true;
      let aboveMs = 0;       // 累计发音时长
      let segStart = 0;      // 当前发音段开始时刻
      const liveFreqEl = el('span', { class: 'tiny muted', text: '—' });
      const peakEl = el('span', { class: 'tiny muted', text: '峰值 —' });

      // ---- 频谱 + 波形 canvas ----
      const specCanvas = el('canvas', { class: 'spec-canvas' }) as HTMLCanvasElement;
      const waveCanvas = el('canvas', { class: 'wave-canvas' }) as HTMLCanvasElement;
      const specWrap = el('div', { class: 'spec-wrap', style: 'display:none' }, [
        thrControl,
        el('div', { class: 'spec-title' }, [el('span', { text: '频谱 (0–4kHz) · 峰值快照' }), liveFreqEl]),
        specCanvas,
        el('div', { class: 'spec-title' }, [el('span', { text: '波形(实时)' }), peakEl]),
        waveCanvas,
        progress,
      ]);

      const specCtx = specCanvas.getContext('2d')!;
      const waveCtx = waveCanvas.getContext('2d')!;

      function drawSpectrum(snap: PeakSnapshot) {
        const w = specCanvas.clientWidth, h = specCanvas.clientHeight;
        specCanvas.width = w * dpr; specCanvas.height = h * dpr;
        const ctx = specCtx;
        ctx.clearRect(0, 0, specCanvas.width, specCanvas.height);
        ctx.strokeStyle = 'rgba(124,92,255,.12)'; ctx.lineWidth = 1;
        for (let i = 1; i < 4; i++) { const y = (h / 4) * i * dpr; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(specCanvas.width, y); ctx.stroke(); }

        const spectrum = powerSpectrum(snap.samples);
        const binHz = snap.sampleRate / snap.samples.length;
        const maxBin = Math.min(spectrum.length, Math.ceil(MAX_FREQ / binHz));
        const barW = specCanvas.width / maxBin;
        let peakBins: { bin: number; mag: number }[] = [];
        for (let i = 2; i < maxBin - 2; i++) {
          if (spectrum[i] > spectrum[i - 1] && spectrum[i] > spectrum[i + 1] && spectrum[i] > spectrum[i - 2] && spectrum[i] > spectrum[i + 2]) {
            peakBins.push({ bin: i, mag: spectrum[i] });
          }
        }
        const maxMag = Math.max(...spectrum.slice(0, maxBin), 1e-9);
        for (let i = 0; i < maxBin; i++) {
          const norm = spectrum[i] / maxMag;
          const bh = norm * specCanvas.height;
          ctx.fillStyle = `hsla(${40 + (1 - norm) * 20},70%,${30 + norm * 30}%,.85)`;
          ctx.fillRect(i * barW, specCanvas.height - bh, Math.max(1, barW - 0.5), bh);
        }
        if (snap.freq > 0 && snap.freq < MAX_FREQ) {
          const x = (snap.freq / MAX_FREQ) * specCanvas.width;
          ctx.strokeStyle = '#ff5470'; ctx.lineWidth = 1 * dpr; ctx.setLineDash([2, 2]);
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, specCanvas.height); ctx.stroke(); ctx.setLineDash([]);
          ctx.fillStyle = '#ff5470'; ctx.font = `${11 * dpr}px sans-serif`;
          ctx.fillText('基频', x + 4, 13 * dpr);
        }
        peakBins.sort((a, b) => b.mag - a.mag);
        const cols = ['#4cc9f0', '#7c5cff', '#3ddc84'];
        ctx.font = `${10 * dpr}px sans-serif`;
        peakBins.slice(0, 3).forEach((p, i) => {
          const f = p.bin * binHz;
          if (f > MAX_FREQ) return;
          const x = (f / MAX_FREQ) * specCanvas.width;
          const y = specCanvas.height - (p.mag / maxMag) * specCanvas.height;
          ctx.strokeStyle = cols[i]; ctx.lineWidth = 1 * dpr; ctx.setLineDash([4, 3]);
          ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, specCanvas.height); ctx.stroke(); ctx.setLineDash([]);
          ctx.fillStyle = cols[i];
          ctx.fillText(`${f.toFixed(0)}Hz`, x + 3, y - 3);
        });
        ctx.fillStyle = 'var(--text-faint)'; ctx.font = `${9 * dpr}px sans-serif`;
        for (let f = 0; f <= MAX_FREQ; f += 1000) {
          const x = (f / MAX_FREQ) * specCanvas.width;
          ctx.fillText(`${f / 1000}k`, x + 2, specCanvas.height - 3);
        }
      }

      function drawWave(samples: Float32Array) {
        const w = waveCanvas.clientWidth, h = waveCanvas.clientHeight;
        waveCanvas.width = w * dpr; waveCanvas.height = h * dpr;
        const ctx = waveCtx;
        ctx.clearRect(0, 0, waveCanvas.width, waveCanvas.height);
        ctx.strokeStyle = 'rgba(124,92,255,.2)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, waveCanvas.height / 2); ctx.lineTo(waveCanvas.width, waveCanvas.height / 2); ctx.stroke();
        ctx.strokeStyle = '#4cc9f0'; ctx.lineWidth = 1.5 * dpr;
        ctx.beginPath();
        const step = samples.length / waveCanvas.width;
        for (let x = 0; x < waveCanvas.width; x++) {
          const v = samples[Math.floor(x * step)] || 0;
          const y = waveCanvas.height / 2 - v * (waveCanvas.height / 2) * 0.9;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      async function start() {
        startBtn.disabled = true;
        try {
          await state.engine.start();
        } catch (e) {
          startBtn.disabled = false;
          showMicError(e);
          return;
        }
        peakRms = 0; snapshotFrame = null; wasBelow = true; aboveMs = 0; segStart = 0;
        resultCard.innerHTML = '';
        startBtn.style.display = 'none';
        stopBtn.style.display = '';
        specWrap.style.display = '';
        progress.style.display = '';
        statusEl.textContent = '录音中…拉一个稳定长音';
        unsub = state.engine.onFrameEvent((f) => {
          const thrRms = (volumeThresholdPct / 100) * 0.2;
          const volPct = Math.min(100, (f.rms / 0.2) * 100);
          const now = performance.now();
          // 每段取峰值:超过阈值→进入新段,记录本段最高音;低于阈值→冻结,等下次超过才刷新
          if (f.rms >= thrRms && f.freq > 0) {
            if (wasBelow) { peakRms = f.rms; wasBelow = false; segStart = now; }
            else { peakRms = Math.max(peakRms, f.rms); }
            // 本段内用当前最高音更新快照并刷新频谱
            snapshotFrame = { samples: f.samples.slice(), sampleRate: f.sampleRate, freq: f.freq, rms: peakRms };
            drawSpectrum(snapshotFrame);
            liveFreqEl.textContent = `${f.freq.toFixed(1)} Hz`;
            peakEl.textContent = `本段峰值 ${(peakRms / 0.2 * 100).toFixed(0)}% · 音量 ${volPct.toFixed(0)}%`;
          } else {
            if (!wasBelow && segStart) { aboveMs += now - segStart; } // 段结束,累计时长
            wasBelow = true;
            peakEl.textContent = `音量 ${volPct.toFixed(0)}% (低于阈值,冻结)`;
          }
          progress.textContent = `已发音 ${(aboveMs / 1000).toFixed(1)} s`;
          drawWave(f.samples);
        });
      }

      function stop() {
        unsub?.(); unsub = null;
        // 若停在 above 状态,把当前段也计入
        if (!wasBelow && segStart) aboveMs += performance.now() - segStart;
        state.engine.stop();
        startBtn.style.display = ''; startBtn.disabled = false;
        stopBtn.style.display = 'none';
        progress.style.display = 'none';
        statusEl.textContent = '分析中…';
        analyze();
      }

      function analyze() {
        if (aboveMs < MIN_TONE_MS) {
          resultCard.innerHTML = `<div class="empty">发音不足 ${(aboveMs / 1000).toFixed(1)}s（需持续 ≥${(MIN_TONE_MS / 1000).toFixed(0)}s），请拉一个稳定长音</div>`;
          statusEl.textContent = '拉一个稳定的长音';
          return;
        }
        if (!snapshotFrame) {
          resultCard.innerHTML = '<div class="empty">未采集到有效峰值，请重试</div>';
          statusEl.textContent = '拉一个稳定的长音';
          return;
        }
        const snap = snapshotFrame;
        const sr = snap.sampleRate;
        const buf = snap.samples; // 2048, 2 的幂,FFT 安全
        const { freq, confidence } = yinDetect(buf, sr);
        const fundamental = freq > 0 ? freq : snap.freq;
        if (fundamental <= 0 || confidence < 0.3) {
          resultCard.innerHTML = '<div class="empty">未能检测到稳定基频，请拉得更稳一些</div>';
          statusEl.textContent = '拉一个稳定的长音';
          return;
        }
        const her = harmonicEnergyRatio(buf, sr, fundamental);
        const formants = extractFormants(buf, sr);
        const snr = estimateSNR(buf, sr, fundamental);
        const f3: [number, number, number] = [formants.f1, formants.f2, formants.f3];
        const herS = herScore(her);
        const resS = resonanceScore(f3, FORMANT_TEMPLATE);
        const raw = timbreScore(her, f3, FORMANT_TEMPLATE);
        const policy = snrPolicy(snr);
        const final = raw * policy.weight;
        const pass = state.prefs.timbreThreshold;
        renderResult({ freq: fundamental, her, formants: f3, snr, herS, resS, raw, final, policy, pass, toneMs: aboveMs });
        statusEl.textContent = '分析完成';
      }

      function renderResult(r: {
        freq: number; her: number; formants: [number, number, number]; snr: number;
        herS: number; resS: number; raw: number; final: number;
        policy: { weight: number; disabled: boolean; warn: boolean }; pass: number; toneMs: number;
      }) {
        const hints: string[] = [];
        if (r.her < 0.6) hints.push('谐波能量偏低 → 尝试加快弓速、增加弓压');
        else if (r.her > 0.85) hints.push('谐波过强略刺耳 → 适当减轻弓压');
        if (r.snr <= 20) hints.push('环境噪声较高，建议靠近麦克风或换安静环境');
        if (hints.length === 0) hints.push('音色状态良好，继续保持');

        const snrTag = r.policy.disabled
          ? el('span', { class: 'tag-bad tiny', text: '🚫 环境过吵，已暂停音色评估' })
          : r.policy.warn
            ? el('span', { class: 'tag-warn tiny', text: '⚠️ 环境较吵，评分仅供参考' })
            : el('span', { class: 'tag-ok tiny', text: '✓ 环境良好' });

        const herBar = el('div', { class: 'volbar' }, [el('div', { class: 'fill', style: `width:${Math.min(100, r.her * 100)}%` })]);

        resultCard.innerHTML = '';
        resultCard.append(el('div', { class: 'card' }, [
          el('div', { class: 'row between' }, [el('h2', { text: '音色分析' }), snrTag]),
          el('div', { class: 'row center', style: 'gap:18px' }, [
            r.policy.disabled ? el('div', { class: 'big-num muted', text: '—' }) : scoreRing(Math.round(r.final), '音色'),
            el('div', { class: 'col tiny' }, [
              el('span', { text: `基频: ${r.freq.toFixed(1)} Hz · 发音 ${(r.toneMs / 1000).toFixed(1)}s` }),
              el('span', { text: `SNR: ${r.snr.toFixed(0)} dB` }),
              el('span', { text: `谐波能量比 HER: ${r.her.toFixed(2)}（理想 0.6–0.85）` }),
              herBar,
              el('span', { text: `共振峰 F1/F2/F3: ${r.formants.map((f) => f.toFixed(0)).join(' / ')} Hz` }),
              el('span', { text: `HER 得分: ${Math.round(r.herS)} · 共振峰得分: ${Math.round(r.resS)}` }),
              el('span', { class: r.final >= r.pass ? 'tag-ok' : 'tag-warn', text: r.final >= r.pass ? '✓ 达到及格线' : `未达及格线（${r.pass}）` }),
            ]),
          ]),
          el('p', { class: 'tiny muted', text: '基于发音峰值时刻的 2048 点快照分析(基频+谐波分步),非长时间累计。' }),
          el('h2', { text: '改进建议' }),
          ...hints.map((h) => el('div', { class: 'tiny muted', text: `• ${h}` })),
          el('p', { class: 'tiny muted', text: '评分 = 0.3×HER + 0.7×共振峰匹配；SNR≤10dB 禁用，10–20dB 降权 50%。' }),
        ]));
        if (r.final >= r.pass && !r.policy.disabled) toast('🎉 音色不错！');
      }

      startBtn.onclick = start;
      stopBtn.onclick = stop;
      cleanup = stop;

      container.append(
        el('div', { class: 'row between', style: 'align-items:baseline' }, [
          el('div', {}, [el('h1', { text: '音色鉴别' }), el('p', { class: 'sub', text: '长音分析 · 谐波能量比 + 共振峰匹配 · SNR 自适应' })]),
          statusEl,
        ]),
        el('div', { class: 'card' }, [el('div', { class: 'row center' }, [startBtn, stopBtn])]),
        specWrap,
        resultCard,
        el('p', { class: 'tiny muted', text: '🔒 音频本地处理，不上传云端。及格线可在设置中调整。' }),
      );
      return undefined;
    },
    unmount() {
      cleanup?.();
      cleanup = null;
    },
  };
}
